import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Clock, Truck, Users } from "lucide-react";
import { Button } from "../components/ui/button";

const ASSETS = {
  before1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/pt40o4qr_20260131_115906.jpg",
  after1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/4sqzggqv_20260131_153350.jpg",
  after2: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/6p0l0ygl_20260131_153419.jpg",
};

const ServicesPage = () => {
  const services = [
    {
      id: "clear_out",
      name: "Garage Clear-Out",
      tagline: "Fast Removal Service",
      description: "A fast and efficient garage clearing service designed to remove clutter and unwanted items so you can reclaim your space.",
      includes: [
        "Removal of loose items from garage",
        "Separation of large items for council pickup",
        "Bagging of rubbish and unwanted items",
        "Basic garage sweep and tidy"
      ],
      pricing: [
        { size: "Single Garage", price: 529 },
        { size: "Double Garage", price: 699 }
      ],
      addOns: [
        { name: "Waste Removal & Tip Disposal", price: "$180-$220 depending on load" },
        { name: "Extra Labour Time", price: "$95 per hour" }
      ],
      image: ASSETS.before1,
      color: "#1a365d"
    },
    {
      id: "reset",
      name: "Garage Reset",
      tagline: "Full Organisation Service",
      description: "A premium garage transformation where items are sorted, organised, and returned to the garage in a clean and structured layout.",
      includes: [
        "Garage cleared zone by zone",
        "Items sorted into Keep / Donate / Dispose",
        "Client guided decision making",
        "Items returned neatly organised",
        "Basic garage clean and reset"
      ],
      pricing: [
        { size: "Single Garage", price: 849 },
        { size: "Double Garage", price: 1049 }
      ],
      addOns: [
        { name: "Extra Hours", price: "$120 per hour" }
      ],
      image: ASSETS.after1,
      color: "#0A0A0A",
      featured: true
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto text-center">
          <span className="caption text-[#1a365d] block mb-4">Our Services</span>
          <h1 className="heading-xl text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Garage Transformation Services
          </h1>
          <p className="body-lg text-slate-400 max-w-2xl mx-auto">
            From quick clear-outs to full garage resets, we have the perfect service 
            to transform your space.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto space-y-24">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
              data-testid={`service-detail-${service.id}`}
            >
              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img 
                  src={service.image}
                  alt={service.name}
                  className="w-full aspect-[4/3] object-cover"
                />
                {service.featured && (
                  <div className="absolute top-4 right-4 bg-[#1a365d] text-white px-4 py-2">
                    <span className="font-bold uppercase text-sm tracking-wider">Most Popular</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <span className="caption text-[#1a365d] block mb-2">{service.tagline}</span>
                <h2 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {service.name}
                </h2>
                <p className="body-lg text-slate-600 mb-8">
                  {service.description}
                </p>

                {/* Includes */}
                <div className="mb-8">
                  <h3 className="font-bold uppercase tracking-wider text-sm mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    {service.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pricing */}
                <div className="mb-8 p-6 bg-slate-50 border-l-4" style={{ borderColor: service.color }}>
                  <h3 className="font-bold uppercase tracking-wider text-sm mb-4">Pricing</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {service.pricing.map((tier, i) => (
                      <div key={i}>
                        <p className="text-sm text-slate-500">{tier.size}</p>
                        <p className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                          From ${tier.price}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add-ons */}
                <div className="mb-8">
                  <h3 className="font-bold uppercase tracking-wider text-sm mb-4">Available Add-Ons</h3>
                  <ul className="space-y-2">
                    {service.addOns.map((addon, i) => (
                      <li key={i} className="flex justify-between text-slate-700 py-2 border-b border-slate-200">
                        <span>{addon.name}</span>
                        <span className="font-semibold">{addon.price}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to={`/booking?service=${service.id}`}>
                  <Button className="btn-primary" data-testid={`book-${service.id}-btn`}>
                    Book {service.name}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-[#1a365d] block mb-4">Why Choose Us</span>
            <h2 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              The Order & Clean Difference
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Clock,
                title: "Same Day Service",
                description: "Most garages completed in a single day. Fast, efficient, professional."
              },
              {
                icon: Users,
                title: "Experienced Team",
                description: "Our trained professionals handle your belongings with care and respect."
              },
              {
                icon: Truck,
                title: "Full Service Option",
                description: "We can arrange waste removal and tip disposal for a complete solution."
              }
            ].map((feature, index) => (
              <div 
                key={index} 
                className="p-8 bg-white border-t-4 border-[#1a365d]"
                data-testid={`feature-${index + 1}`}
              >
                <feature.icon size={40} className="text-[#1a365d] mb-4" />
                <h3 className="heading-md text-[#0A0A0A] mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#1a365d]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading-lg text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Not Sure Which Service?
          </h2>
          <p className="body-lg text-slate-300 mb-8">
            Complete our free garage assessment and we'll recommend the best service 
            for your needs with an instant quote. Servicing Sydney & surrounding regions.
          </p>
          <Link to="/booking?assessment=true" data-testid="services-cta-btn">
            <Button className="bg-white text-[#1a365d] hover:bg-slate-100 px-8 py-4 uppercase font-bold tracking-wider text-sm transition-all">
              Get Free Assessment
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
